import styled, { css } from "styled-components";

import { themeColors } from "js/react/sharedStyles";

const baseText = css`
    font-family: "Source Sans Pro";
    font-size: 15px;
    font-weight: 400;
    line-height: normal;
    text-transform: none;
    align-self: flex-start;
    color: ${themeColors.darkestGray};
`;

const headerText = css`
    font-size: 24px;
    font-weight: 600;
    line-height: 120%;
    letter-spacing: 0.5px;
`;

const subHeaderText = css`
    font-size: 16px;
    font-weight: 400;
    line-height: 125%;
    letter-spacing: 0.5px;
`;

const descriptionText = css`
    font-size: 16px;
    font-weight: 600;
`;

const uppercaseLabelText = css`
    font-size: 12px;
    text-transform: uppercase;
    font-weight: 600;
`;

const noteText = css`
    font-size: 12px;
    font-weight: 400;
    color: ${themeColors.mediumGray};
`;

const regularText = css`
    font-size: 13px;
    font-weight: 600;
`;

const smallText = css`
    font-size: 9px;
    color: ${themeColors.mediumGray};
`;

const bodyText = css`
    font-size: 16px;
    font-weight: 400
    letter-spacing: -0.02em;
    line-height: 1.6;
    color: ${themeColors.darkGray};
`;

const popupLabelText = css`
    color: ${themeColors.mediumGray};
    font-size: 11px;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 1px;
    text-transform: uppercase;
    text-align: right;
`;

const popupValueText = css`
    color: ${themeColors.darkGray};
    font-size: 16px;
    font-weight: 400;
    line-height: 125%;
`;

const popupDescriptionText = css`
    color: ${themeColors.darkestGray};
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
    line-height: 19px; /* 158.333% */
    text-transform: uppercase; 
`;

const labelText = css`
    font-size: 14px;
    font-weight: 400;
    line-height: 145%;
    letter-spacing: -0.144px;
    color: #666;
`;

const largeLabelText = css`
    font-size: 14px;
    font-weight: 400;
    line-height: 145%;
    letter-spacing: -0.28px;
    margin: 0;
    color: #666;
`;

const sectionHeadingText = css`
    font-size: 18px;
    font-weight: 600;
    line-height: 120%;
    color: #222;
`;

const nonWrappingText = css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const strikethroughText = css`
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
    letter-spacing: -0.18px;
    text-decoration-line: line-through;
    color: #666;
`;

const linkText = css`
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 145%;
    letter-spacing: -0.28px;
    text-decoration-line: underline;
    color: ${themeColors.ui_blue};
    cursor: pointer;
`;

const linkSmallText = css`
    font-size: 11px;
    font-style: normal;
    font-weight: 400;
    line-height: 125%;
    color: ${themeColors.ui_blue};
    cursor: pointer;
`;

const errorBlockText = css`
    color: white;
    background: orangered;
    letter-spacing: 0px;
    padding: 8px 15px;
    margin-top: 20px;
    font-size: 15px;
    font-weight: 600;
    display: inline-block;
`;

const badgeText = css`
    background: #7fb314;
    color: white;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;
    padding: 2px 8px;
    display: inline-block;
`;

const dialogTitleText = css`
    font-size: 24px;
    font-weight: 600;
    line-height: 1.2;
    color: #444444;
`;

const dialogSubtitleText = css`
    font-size: 14px;
    font-weight: 400;
    line-height: 1.25;
    color: #666;
`;

const summaryTitleText = css`
    font-size: 10px;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #444444;
`;

const disclaimerText = css`
    font-size: 11px;
    font-weight: 400;
    font-style: normal;
    line-height: 125%;
    color: #666;
`;

const helperText = css`
    font-size: 14px;
    font-weight: 400;
    line-height: 1.25;
    color: #666;
`;

const sectionLabelText = css`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
    letter-spacing: 0.5px;
    color: #444444;
`;

interface TextContentProps {
    header?: boolean,
    subHeader?: boolean,
    description?: boolean,
    badge?: boolean,
    dialogTitle?: boolean,
    dialogSubtitle?: boolean,
    summaryTitle?: boolean,
    disclaimer?: boolean,
    helper?: boolean,
    sectionLabel?: boolean,
    uppercase?: boolean,
    center?: boolean,
    note?: boolean,
    regular?: boolean,
    error?: boolean,
    errorBlock?: boolean,
    bold?: boolean,
    normal?: boolean,
    italic?: boolean,
    color?: string,
    small?: boolean,
    dark?: boolean,
    light?: boolean,
    noWrap?: boolean,
    body?: boolean,
    popupLabel?: boolean,
    popupValue?: boolean,
    popupDescription?: boolean,
    label?: boolean,
    largeLabel?: boolean,
    sectionHeading?: boolean,
    strikethrough?: boolean,
    link?: boolean,
    linkSmall?: boolean,
    href?: string,
    onClick?: () => void,
    padding?: string,
    margin?: string,
}

export const TextContent = styled.span.attrs<TextContentProps>(props => ({
    as: props.link && props.href ? "a" : "span",
    href: props.link && props.href ? props.href : undefined,
    onClick: props.link && props.onClick ? props.onClick : undefined,
})) <TextContentProps>`
    ${baseText}
    ${props => props.header && headerText}
    ${props => props.subHeader && subHeaderText}
    ${props => props.description && descriptionText}
    ${props => props.badge && badgeText}
    ${props => props.dialogTitle && dialogTitleText}
    ${props => props.dialogSubtitle && dialogSubtitleText}
    ${props => props.summaryTitle && summaryTitleText}
    ${props => props.disclaimer && disclaimerText}
    ${props => props.helper && helperText}
    ${props => props.sectionLabel && sectionLabelText}
    ${props => props.uppercase && uppercaseLabelText}
    ${props => props.note && noteText}
    ${props => props.regular && regularText}
    ${props => props.small && smallText}
    ${props => props.body && bodyText}
    ${props => props.popupLabel && popupLabelText}
    ${props => props.popupValue && popupValueText}
    ${props => props.popupDescription && popupDescriptionText}
    ${props => props.label && labelText}
    ${props => props.largeLabel && largeLabelText}
    ${props => props.sectionHeading && sectionHeadingText}
    ${props => props.noWrap && nonWrappingText}
    ${props => props.strikethrough && strikethroughText}
    ${props => props.link && linkText}
    ${props => props.linkSmall && linkSmallText}
    ${props => props.errorBlock && errorBlockText}
    ${props => props.color && css`color: ${props.color};`}
    ${props => props.dark && css`color: ${themeColors.darkGray};`}
    ${props => props.light && css`color: ${themeColors.mediumGray};`}
    ${props => props.error && css`color: ${themeColors.warning};`}
    ${props => props.bold && css`font-weight: 600;`}
    ${props => props.normal && css`font-weight: 400;`}
    ${props => props.italic && css`font-style: italic; font-weight: 400;`}
    ${props => props.center && css`align-self: center;`}
    ${props => props.padding && css`padding: ${props.padding};`}
    ${props => props.margin && css`margin: ${props.margin};`}
`;
